package com.hyperether.goodjob.scenes.jobs.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.TabRowDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import com.hyperether.goodjob.models.JobFilter
import com.hyperether.goodjob.models.UserTeam
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.assignees
import com.hyperether.goodjob.resources.job_status_cancelled
import com.hyperether.goodjob.resources.job_status_completed
import com.hyperether.goodjob.resources.job_status_draft
import com.hyperether.goodjob.resources.location
import com.hyperether.goodjob.resources.schedule
import com.hyperether.goodjob.resources.status
import com.hyperether.goodjob.scenes.components.FilterSectionWeb
import com.hyperether.goodjob.scenes.components.disableMouseDragScrollOnWeb
import com.hyperether.goodjob.scenes.components.toggleSelection
import com.hyperether.goodjob.scenes.components.verticalScrollbar
import com.hyperether.goodjob.theme.InFilterBorder
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.resources.stringResource

@Composable
fun FilterJobsDialogWeb(
    modifier: Modifier = Modifier,
    filterMap: Map<List<String>, Set<String>>,
    onApply: (map: Map<JobFilter, Set<String>>) -> Unit,
    isEmployee: Boolean,
    userTeams: List<UserTeam?>?
) {
    val status: List<String> =
        filterMap.keys.elementAtOrNull(JobFilter.STATUS.value) ?: emptyList()
    val activeStatusSet = filterMap.keys.elementAtOrNull(JobFilter.STATUS.value)?.toMutableSet()
    activeStatusSet?.removeAll(
        setOf(
            stringResource(Res.string.job_status_completed),
            stringResource(Res.string.job_status_cancelled),
            stringResource(Res.string.job_status_draft)
        )
    )
    val initialSelectedStatus: Set<String> = if (FilterDialogStateManager.isFirstOpen) {
        activeStatusSet ?: emptySet()
        if (activeStatusSet?.isEmpty() == true) {
            status.toSet()
        } else {
            activeStatusSet ?: emptySet()
        }
    } else {
        filterMap.values
            .elementAtOrNull(JobFilter.STATUS.value)
            ?.takeIf { it.isNotEmpty() }
            ?: emptySet()
    }
    val employees: List<String> =
        filterMap.keys.elementAtOrNull(JobFilter.ASSIGNEE.value) ?: emptyList()
    val employeeTeams = employees.toSet().intersect(
        userTeams?.mapNotNull { it?.value }?.toSet().orEmpty()
    ).toList()
    val initialSelectedEmployees: Set<String> =
        filterMap.values.elementAtOrNull(JobFilter.ASSIGNEE.value) ?: emptySet()

    val locations: List<String> =
        filterMap.keys.elementAtOrNull(JobFilter.LOCATION.value) ?: emptyList()
    val initialSelectedLocations: Set<String> =
        filterMap.values.elementAtOrNull(JobFilter.LOCATION.value) ?: emptySet()

    val schedule: List<String> =
        filterMap.keys.elementAtOrNull(JobFilter.SCHEDULE.value) ?: emptyList()
    val initialSelectedSchedule: Set<String> =
        filterMap.values.elementAtOrNull(JobFilter.SCHEDULE.value) ?: emptySet()

    // Initialize state with the current selections
    var selectedStatus by remember { mutableStateOf(initialSelectedStatus) }
    var selectedEmployees by remember { mutableStateOf(initialSelectedEmployees) }
    var selectedLocations by remember { mutableStateOf(initialSelectedLocations) }
    var selectedSchedule by remember { mutableStateOf(initialSelectedSchedule) }

    Column(
        modifier = modifier
            .fillMaxHeight()
            .background(Color.White)
    ) {
        val scrollState = rememberScrollState()
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .verticalScrollbar(scrollState)
                .verticalScroll(scrollState)
                .disableMouseDragScrollOnWeb()
        ) {
            if (status.any { it.isNotBlank() }) {
                Spacer(modifier = Modifier.height(15.toScaleDp()))

                FilterSectionWeb(
                    title = stringResource(Res.string.status),
                    options = status,
                    selectedOptions = selectedStatus,
                    onOptionSelected = { option ->
                        selectedStatus = toggleSelection(selectedStatus, option)
                        onApply(
                            mapOf(
                                JobFilter.STATUS to selectedStatus,
                                JobFilter.ASSIGNEE to selectedEmployees,
                                JobFilter.LOCATION to selectedLocations,
                                JobFilter.SCHEDULE to selectedSchedule
                            )
                        )
                    }
                )
            }

            if (!isEmployee) {
                if (employees.any { it.isNotBlank() }) {
                    Spacer(modifier = Modifier.height(10.toScaleDp()))
                    TabRowDefaults.Divider(
                        color = InFilterBorder,
                        thickness = 1.toScaleDp(),
                        modifier = Modifier.fillMaxWidth()
                    )
                    FilterSectionWeb(
                        title = stringResource(Res.string.assignees),
                        options = employees,
                        selectedOptions = selectedEmployees,
                        onOptionSelected = { option ->
                            selectedEmployees = toggleSelection(selectedEmployees, option)
                            onApply(
                                mapOf(
                                    JobFilter.STATUS to selectedStatus,
                                    JobFilter.ASSIGNEE to selectedEmployees,
                                    JobFilter.LOCATION to selectedLocations,
                                    JobFilter.SCHEDULE to selectedSchedule
                                )
                            )
                        }
                    )
                }
            } else {
                if (employeeTeams.any { it.isNotBlank() }) {
                    Spacer(modifier = Modifier.height(10.toScaleDp()))
                    TabRowDefaults.Divider(
                        color = InFilterBorder,
                        thickness = 1.toScaleDp(),
                        modifier = Modifier.fillMaxWidth()
                    )
                    FilterSectionWeb(
                        title = stringResource(Res.string.assignees),
                        options = employeeTeams,
                        selectedOptions = selectedEmployees,
                        onOptionSelected = { option ->
                            selectedEmployees = toggleSelection(selectedEmployees, option)
                            onApply(
                                mapOf(
                                    JobFilter.STATUS to selectedStatus,
                                    JobFilter.ASSIGNEE to selectedEmployees,
                                    JobFilter.LOCATION to selectedLocations,
                                    JobFilter.SCHEDULE to selectedSchedule
                                )
                            )
                        }
                    )
                }
            }

            if (locations.any { it.isNotBlank() }) {
                Spacer(modifier = Modifier.height(10.toScaleDp()))
                TabRowDefaults.Divider(
                    color = InFilterBorder,
                    thickness = 1.toScaleDp(),
                    modifier = Modifier.fillMaxWidth()
                )
                FilterSectionWeb(
                    title = stringResource(Res.string.location),
                    options = locations,
                    selectedOptions = selectedLocations,
                    onOptionSelected = { option ->
                        selectedLocations = toggleSelection(selectedLocations, option)
                        onApply(
                            mapOf(
                                JobFilter.STATUS to selectedStatus,
                                JobFilter.ASSIGNEE to selectedEmployees,
                                JobFilter.LOCATION to selectedLocations,
                                JobFilter.SCHEDULE to selectedSchedule
                            )
                        )
                    }
                )
            }

            if (schedule.any { it.isNotBlank() }) {
                Spacer(modifier = Modifier.height(10.toScaleDp()))
                TabRowDefaults.Divider(
                    color = InFilterBorder,
                    thickness = 1.toScaleDp(),
                    modifier = Modifier.fillMaxWidth()
                )
                FilterSectionWeb(
                    title = stringResource(Res.string.schedule),
                    options = schedule,
                    selectedOptions = selectedSchedule,
                    onOptionSelected = { option ->
                        selectedSchedule = toggleSelection(selectedSchedule, option)
                        onApply(
                            mapOf(
                                JobFilter.STATUS to selectedStatus,
                                JobFilter.ASSIGNEE to selectedEmployees,
                                JobFilter.LOCATION to selectedLocations,
                                JobFilter.SCHEDULE to selectedSchedule
                            )
                        )
                    }
                )
            }
        }
    }
}